import type { DeckAttachWall, DeckConfig, SaunaDimensions } from '../../engines/types';

export interface DeckRect {
  width: number;
  depth: number;
  centerX: number;
  centerZ: number;
}

function round2(value: number): number {
  return Number(value.toFixed(2));
}

function getSaunaSpanAlongAttachWall(
  attachWall: DeckAttachWall,
  sauna: SaunaDimensions,
): number {
  return attachWall === 'left' || attachWall === 'right'
    ? sauna.length
    : sauna.width;
}

function getSaunaSideRun(
  attachWall: DeckAttachWall,
  sauna: SaunaDimensions,
): number {
  return attachWall === 'left' || attachWall === 'right'
    ? sauna.width
    : sauna.length;
}

export function getDeckFootprintRects(
  config: DeckConfig,
  sauna: SaunaDimensions,
): DeckRect[] {
  const rects: DeckRect[] = [
    {
      width: config.width,
      depth: config.depth,
      centerX: 0,
      centerZ: config.depth / 2,
    },
  ];

  const saunaSpan = getSaunaSpanAlongAttachWall(config.attachWall, sauna);
  const sideRun = getSaunaSideRun(config.attachWall, sauna);

  if (config.shape === 'l-shape' && config.armDepth && config.armWidth) {
    rects.push({
      width: config.armWidth,
      depth: config.depth + config.armDepth,
      centerX: saunaSpan / 2 + config.armWidth / 2,
      centerZ: (config.depth - config.armDepth) / 2,
    });
  }

  if (config.shape === 'wrap-front' && config.wrapDepth) {
    rects.push(
      {
        width: config.wrapDepth,
        depth: config.depth,
        centerX: -(config.width / 2 + config.wrapDepth / 2),
        centerZ: config.depth / 2,
      },
      {
        width: config.wrapDepth,
        depth: config.depth,
        centerX: config.width / 2 + config.wrapDepth / 2,
        centerZ: config.depth / 2,
      },
    );
  }

  if (config.shape === 'wrap-sides' && config.wrapDepth) {
    rects.push(
      {
        width: config.wrapDepth,
        depth: config.depth + sideRun,
        centerX: -(saunaSpan / 2 + config.wrapDepth / 2),
        centerZ: (config.depth - sideRun) / 2,
      },
      {
        width: config.wrapDepth,
        depth: config.depth + sideRun,
        centerX: saunaSpan / 2 + config.wrapDepth / 2,
        centerZ: (config.depth - sideRun) / 2,
      },
    );
  }

  return rects;
}

export function measureDeckFootprint(rects: DeckRect[]) {
  if (rects.length === 0) {
    return { areaSqm: 0, perimeterM: 0 };
  }

  const xBreaks = Array.from(new Set(rects.flatMap((rect) => [
    rect.centerX - rect.width / 2,
    rect.centerX + rect.width / 2,
  ]))).sort((a, b) => a - b);
  const zBreaks = Array.from(new Set(rects.flatMap((rect) => [
    rect.centerZ - rect.depth / 2,
    rect.centerZ + rect.depth / 2,
  ]))).sort((a, b) => a - b);

  const covered: boolean[][] = [];
  let area = 0;

  for (let xi = 0; xi < xBreaks.length - 1; xi += 1) {
    covered[xi] = [];
    for (let zi = 0; zi < zBreaks.length - 1; zi += 1) {
      const x1 = xBreaks[xi];
      const x2 = xBreaks[xi + 1];
      const z1 = zBreaks[zi];
      const z2 = zBreaks[zi + 1];
      const centerX = (x1 + x2) / 2;
      const centerZ = (z1 + z2) / 2;
      const isCovered = rects.some((rect) => (
        centerX >= rect.centerX - rect.width / 2
        && centerX <= rect.centerX + rect.width / 2
        && centerZ >= rect.centerZ - rect.depth / 2
        && centerZ <= rect.centerZ + rect.depth / 2
      ));
      covered[xi][zi] = isCovered;
      if (isCovered) {
        area += (x2 - x1) * (z2 - z1);
      }
    }
  }

  let perimeter = 0;
  for (let xi = 0; xi < xBreaks.length - 1; xi += 1) {
    for (let zi = 0; zi < zBreaks.length - 1; zi += 1) {
      if (!covered[xi][zi]) continue;

      const cellWidth = xBreaks[xi + 1] - xBreaks[xi];
      const cellDepth = zBreaks[zi + 1] - zBreaks[zi];

      if (xi === 0 || !covered[xi - 1][zi]) perimeter += cellDepth;
      if (xi === xBreaks.length - 2 || !covered[xi + 1][zi]) perimeter += cellDepth;
      if (zi === 0 || !covered[xi][zi - 1]) perimeter += cellWidth;
      if (zi === zBreaks.length - 2 || !covered[xi][zi + 1]) perimeter += cellWidth;
    }
  }

  return {
    areaSqm: round2(area),
    perimeterM: round2(perimeter),
  };
}

/**
 * One straight run of deck edge that faces open ground, in deck-local
 * coordinates (attach wall along z = 0, deck extending toward +z).
 */
export interface DeckEdgeSegment {
  /** Axis the edge runs along. */
  axis: 'x' | 'z';
  /** Coordinate of the edge line on the other axis. */
  at: number;
  from: number;
  to: number;
  /** Which way (+1 / -1, on the other axis) open ground lies. */
  outward: 1 | -1;
}

/**
 * The deck edges that need a kantbräda: every boundary of the deck union that
 * does not butt the sauna itself. The sauna occupies
 * x ∈ [-span/2, span/2], z ∈ [-sideRun, 0] in deck-local coordinates, so the
 * attach-wall edge and a wrap/L arm's inner edge along a side wall are closed,
 * while a deck wider than the wall still gets fascia on the overhanging part.
 */
export function getDeckOpenEdges(
  config: DeckConfig,
  sauna: SaunaDimensions,
): DeckEdgeSegment[] {
  const rects = getDeckFootprintRects(config, sauna);
  if (rects.length === 0) return [];
  const span = getSaunaSpanAlongAttachWall(config.attachWall, sauna);
  const sideRun = getSaunaSideRun(config.attachWall, sauna);
  const saunaRect: DeckRect = {
    width: span,
    depth: sideRun,
    centerX: 0,
    centerZ: -sideRun / 2,
  };

  const key = (value: number) => Math.round(value * 1e6) / 1e6;
  const breaks = (lo: (r: DeckRect) => number, hi: (r: DeckRect) => number) =>
    Array.from(
      new Set(
        [...rects, saunaRect].flatMap((rect) => [key(lo(rect)), key(hi(rect))]),
      ),
    ).sort((a, b) => a - b);
  const xBreaks = breaks(
    (r) => r.centerX - r.width / 2,
    (r) => r.centerX + r.width / 2,
  );
  const zBreaks = breaks(
    (r) => r.centerZ - r.depth / 2,
    (r) => r.centerZ + r.depth / 2,
  );

  const contains = (rect: DeckRect, x: number, z: number) =>
    Math.abs(x - rect.centerX) < rect.width / 2 &&
    Math.abs(z - rect.centerZ) < rect.depth / 2;
  type Cell = 'deck' | 'sauna' | 'open';
  const cellAt = (xi: number, zi: number): Cell => {
    if (
      xi < 0 ||
      zi < 0 ||
      xi >= xBreaks.length - 1 ||
      zi >= zBreaks.length - 1
    ) {
      return 'open';
    }
    const x = (xBreaks[xi] + xBreaks[xi + 1]) / 2;
    const z = (zBreaks[zi] + zBreaks[zi + 1]) / 2;
    if (rects.some((rect) => contains(rect, x, z))) return 'deck';
    return contains(saunaRect, x, z) ? 'sauna' : 'open';
  };

  const pieces: DeckEdgeSegment[] = [];
  for (let xi = 0; xi < xBreaks.length - 1; xi += 1) {
    for (let zi = 0; zi < zBreaks.length - 1; zi += 1) {
      if (cellAt(xi, zi) !== 'deck') continue;
      const [x1, x2, z1, z2] = [
        xBreaks[xi],
        xBreaks[xi + 1],
        zBreaks[zi],
        zBreaks[zi + 1],
      ];
      if (cellAt(xi - 1, zi) === 'open')
        pieces.push({ axis: 'z', at: x1, from: z1, to: z2, outward: -1 });
      if (cellAt(xi + 1, zi) === 'open')
        pieces.push({ axis: 'z', at: x2, from: z1, to: z2, outward: 1 });
      if (cellAt(xi, zi - 1) === 'open')
        pieces.push({ axis: 'x', at: z1, from: x1, to: x2, outward: -1 });
      if (cellAt(xi, zi + 1) === 'open')
        pieces.push({ axis: 'x', at: z2, from: x1, to: x2, outward: 1 });
    }
  }

  // Merge the per-cell pieces into continuous runs along each edge line.
  pieces.sort(
    (a, b) =>
      a.axis.localeCompare(b.axis) ||
      a.at - b.at ||
      a.outward - b.outward ||
      a.from - b.from,
  );
  const merged: DeckEdgeSegment[] = [];
  for (const piece of pieces) {
    const last = merged.at(-1);
    if (
      last &&
      last.axis === piece.axis &&
      last.at === piece.at &&
      last.outward === piece.outward &&
      Math.abs(last.to - piece.from) < 1e-6
    ) {
      last.to = piece.to;
    } else {
      merged.push({ ...piece });
    }
  }
  return merged;
}
